// eslint-disable-next-line import/no-extraneous-dependencies
import { useMemo } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useWixModules, useEnvironment } from '@wix/sdk-react';
import { dashboard, EnvironmentState } from '@wix/dashboard';
import type {
  ReactSDK,
  UseContainerMenuItems,
  UseContainerWidgets,
  UseWidget,
} from './types';

export { ReactSDK };
export * from '@wix/dashboard';
export { withDashboard } from './withDashboard';
export { PageLink } from './PageLink';

/**
 * Returns an object with methods to interact with the dashboard.
 */
export const useDashboard = (): ReactSDK => {
  const sdk = useWixModules(dashboard);

  return useMemo(() => {
    return {
      showToast: sdk.showToast,
      navigate: sdk.navigate,
      navigateBack: sdk.navigateBack,
      openModal: sdk.openModal,
      closeModal: sdk.closeModal,
      getAccessToken: sdk.getAccessToken,
      getPageUrl: sdk.getPageUrl,
      setPageTitle: sdk.setPageTitle,
      openMediaManager: sdk.openMediaManager,
      addSitePlugin: sdk.addSitePlugin,
      onBeforeUnload: sdk.onBeforeUnload,
      requestFeedback: sdk.requestFeedback,
      onLayerStateChange: sdk.onLayerStateChange,
    };
  }, [sdk]);
};

export const useWidget: UseWidget = (componentId, options?) => {
  const channel = useHostChannelDirectly() as { useWidget: UseWidget };
  return channel.useWidget(componentId, options);
};

export const useContainerWidgets: UseContainerWidgets = (
  componentId,
  options?,
) => {
  const channel = useHostChannelDirectly() as {
    useContainerWidgets: UseContainerWidgets;
  };
  return channel.useContainerWidgets(componentId, options);
};

export const useContainerMenuItems: UseContainerMenuItems = (
  componentId: string,
  options?,
) => {
  const channel = useHostChannelDirectly() as {
    useContainerMenuItems: UseContainerMenuItems;
  };
  return channel.useContainerMenuItems(componentId, options);
};

const useHostChannelDirectly = () => {
  const reactDashboardModule = {
    /*
    Creating custom react module, so we can access host api.
    These methods don't exist in vanilla dashboard sdk, so we need a custom module to extract them from the host prop passes to apps.
    */
    __type: 'host',
    create: (host: { channel: unknown }) => host.channel,
  } as const;
  return useWixModules(reactDashboardModule);
};

export const usePageLocation = (): EnvironmentState['pageLocation'] => {
  const environment = useEnvironment<EnvironmentState>();
  const { pageId, pathname, search, hash } = environment.pageLocation;
  return useMemo(
    () => ({ pageId, pathname, search, hash }),
    [pageId, pathname, search, hash],
  );
};

export const useLanguage = (): EnvironmentState['language'] => {
  const { language } = useEnvironment<EnvironmentState>();
  return language;
};
